import {
  DefinitionActionKindEnum,
  type ExchangeRate2Response,
} from '@acme/api';
import type { BreadcrumbItem } from '@acme/foundation';
import { InfoAlert, formatNumber, useModal } from '@acme/foundation';
import {
  $agentContract,
  $application,
  $applicationLiquidityProviders,
  $contract,
  $isLoading,
  $liquidityProviders,
  applicationOpened,
  liquidityProvidersLoadRequested,
} from '@entities/application';
import {
  SelectProviderModal,
  type SelectProviderModalRefImperativeHandleType,
} from '@features/selectProviderModal';
import { useUnit } from 'effector-react';
import type { MouseEvent } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  $currentAmount,
  $dealParticipantRows,
  $isOrderDisabled,
  $orderAmount,
  $pendingAllocations,
  $submitError,
  pendingAllocationRemoved,
  pendingAllocationSet,
  submitErrorSet,
  submitOrderFx,
} from '../model';

let lastLiquidityProvidersLoadId: string | null = null;

export function useApplicationProvidersPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openConfirmModal } = useModal();
  const ref = useRef<SelectProviderModalRefImperativeHandleType>(null);

  const {
    application,
    agentContract,
    providers,
    liquidityProviders,
    isLoading,
    currentAmount,
    orderAmount,
    isDisabled,
    submitError,
    dealParticipantRows,
    onOpened,
    onLiquidityProvidersLoad,
    onPendingAllocationSet,
    onPendingAllocationRemoved,
    onSubmitOrder,
    contract,
  } = useUnit({
    application: $application,
    agentContract: $agentContract,
    providers: $applicationLiquidityProviders,
    liquidityProviders: $liquidityProviders,
    isLoading: $isLoading,
    currentAmount: $currentAmount,
    orderAmount: $orderAmount,
    isDisabled: $isOrderDisabled,
    submitError: $submitError,
    dealParticipantRows: $dealParticipantRows,
    onOpened: applicationOpened,
    onLiquidityProvidersLoad: liquidityProvidersLoadRequested,
    onPendingAllocationSet: pendingAllocationSet,
    onPendingAllocationRemoved: pendingAllocationRemoved,
    onSubmitOrder: submitOrderFx,
    contract: $contract,
  });

  useEffect(() => {
    if (!id) return;
    if (lastLiquidityProvidersLoadId === id) return;
    lastLiquidityProvidersLoadId = id;
    onLiquidityProvidersLoad({ id });
  }, [id, onLiquidityProvidersLoad]);

  const requiredAmount =
    agentContract?.cryptoAmount ?? contract?.cryptoAmount ?? 0;

  const getMaxAmountForProvider = (provider: ExchangeRate2Response): number => {
    const required = requiredAmount;
    const total = currentAmount + orderAmount;
    const pending = $pendingAllocations.getState();
    const currentForProvider = pending[provider.id as string] ?? 0;
    const remaining = required - total + currentForProvider;
    const providerMax = provider.amount ?? 0;
    return Math.max(0, Math.min(providerMax, remaining));
  };

  const displayCurrencyName =
    agentContract?.cryptoCurrency?.name ??
    (contract?.cryptocurrency as { name?: string } | undefined)?.name ??
    '';

  const handleSelectProviders = async () => {
    submitErrorSet(null);
    const isFullAmount = orderAmount >= requiredAmount;

    const confirmContent = isFullAmount ? (
      <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
        Продолжить подтверждение поставщиков ликвидности?
      </p>
    ) : (
      <div className="space-y-4">
        <p className="text-base leading-relaxed text-gray-500 dark:text-gray-400">
          Приобретаемая сумма меньше требуемой, заявка не сможет быть завершена,
          пока не будет {formatNumber(requiredAmount)} {displayCurrencyName}.
          Продолжить подтверждение поставщиков ликвидности?
        </p>
        <InfoAlert
          variant="warning"
          title="Внимание!"
          description="По правилам экспериментального режима цифровая валюта может хранится 90 календарных дней, далее обязана быть продана."
        />
      </div>
    );

    const confirmed = await openConfirmModal({
      title: 'Подтверждение',
      content: confirmContent,
      okBtn: 'Продолжить',
      cancelBtn: 'Отмена',
    });

    if (!confirmed || !application?.actions || !id) return;

    const action = application.actions.find(
      (e) => e.kind === DefinitionActionKindEnum.Form
    );
    if (!action?.id) return;

    onSubmitOrder({
      applicationId: id,
      actionId: action.id,
      liquidityAllocations: $pendingAllocations.getState(),
    });
  };

  const handleSelectProvider = async (
    e: MouseEvent<HTMLElement>,
    provider: ExchangeRate2Response
  ) => {
    e.stopPropagation();
    submitErrorSet(null);

    try {
      const maxAmount = getMaxAmountForProvider(provider);
      await openConfirmModal({
        title: 'Приобретаемая сумма',
        content: (
          <SelectProviderModal
            provider={provider}
            ref={ref}
            maxAmount={maxAmount}
          />
        ),
        okBtn: 'Выбрать поставщика',
        cancelBtn: 'Отмена',
        onConfirmCb: () => {
          const value = ref.current?.getValue();
          if (value != null && provider?.id) {
            onPendingAllocationSet({
              providerId: provider.id as string,
              value,
            });
          }
        },
      });
    } catch (error) {
      submitErrorSet(
        error instanceof Error ? error.message : 'Не удалось выбрать поставщика'
      );
    }
  };

  const handleChangePendingAmount = async (providerId: string) => {
    const provider = liquidityProviders.find((p) => p.id === providerId);
    if (!provider) return;
    submitErrorSet(null);

    try {
      const maxAmount = getMaxAmountForProvider(provider);
      const pending = $pendingAllocations.getState();
      const initialValue = pending[providerId];
      await openConfirmModal({
        title: 'Приобретаемая сумма',
        content: (
          <SelectProviderModal
            provider={provider}
            ref={ref}
            maxAmount={maxAmount}
            initialValue={initialValue}
          />
        ),
        okBtn: 'Сохранить',
        cancelBtn: 'Отмена',
        onConfirmCb: () => {
          const value = ref.current?.getValue();
          if (value != null) {
            onPendingAllocationSet({ providerId, value });
          }
        },
      });
    } catch (error) {
      submitErrorSet(
        error instanceof Error ? error.message : 'Не удалось изменить сумму'
      );
    }
  };

  const handleRemovePending = (providerId: string) => {
    onPendingAllocationRemoved(providerId);
  };

  const breadcrumbItems = useMemo(() => {
    const items: BreadcrumbItem[] = [
      { label: 'Главная', onClick: () => navigate('/dashboard') },
    ];
    if (application) {
      items.push(
        {
          label: `${application.code}`,
          onClick: () => navigate(`/application/${application.id}`),
        },
        { label: 'Выбрать поставщика' }
      );
    } else {
      items.push({ label: 'Выбрать поставщика' });
    }
    return items;
  }, [navigate, application?.code, application?.id]);

  return {
    id,
    submitError,
    application,
    agentContract,
    providers,
    liquidityProviders,
    isLoading,
    currentAmount,
    orderAmount,
    isDisabled,
    breadcrumbItems,
    dealParticipantRows,
    handleSelectProviders,
    handleSelectProvider,
    handleChangePendingAmount,
    handleRemovePending,
    onOpened,
    contract,
  };
}
